import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/CustomerForm.css';

const CustomerForm = ({ onCustomerAdded, onCancel, isSeller }) => {
  const [formData, setFormData] = useState({
    cus_name: '',
    cus_rfc: '',
    cus_domicilio: '',
    cus_zipcode: '',
    regimenfiscal_id: '',
    cus_email: '',
    cus_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [regimenesFiscales, setRegimenesFiscales] = useState([]);
  const [loadingRegimenes, setLoadingRegimenes] = useState(true);

  useEffect(() => {
    const fetchRegimenesFiscales = async () => {
      try {
        setLoadingRegimenes(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/customers/regimenes-fiscales', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setRegimenesFiscales(response.data);
      } catch (error) {
        console.error('Error al obtener regímenes fiscales:', error);
        setError('Error al cargar los regímenes fiscales. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingRegimenes(false);
      }
    };

    fetchRegimenesFiscales();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }

      // Validar campos requeridos
      if (!formData.cus_name) {
        setError('El nombre/razón social es obligatorio');
        setLoading(false);
        return;
      }

      // Usar endpoint diferente según el rol del usuario
      let response;
      if (isSeller) {
        console.log('DEBUG: Usando endpoint de seller');
        response = await axios.post('/api/customers/seller', formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        // Determinar el endpoint según el rol del usuario
        const token = localStorage.getItem('token');
        let userRole = null;
        
        // Obtener rol del token JWT
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            userRole = payload.role;
          } catch (e) {
            console.error('Error parsing token:', e);
          }
        }
        
        console.log('DEBUG: userRole desde token:', userRole);
        console.log('DEBUG: formData enviado:', formData);
        
        const endpoint = userRole === 'manager' ? '/api/supervisors/customers' : '/api/customers';
        console.log('DEBUG: Endpoint seleccionado:', endpoint);
        
        response = await axios.post(endpoint, formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('DEBUG: Respuesta recibida:', response);
      }

      if (onCustomerAdded) {
        onCustomerAdded(response.data);
      }
      setFormData({
        cus_name: '',
        cus_rfc: '',
        cus_domicilio: '',
        cus_zipcode: '',
        regimenfiscal_id: '',
        cus_email: '',
        cus_phone: ''
      });
    } catch (error) {
      console.error('Error al crear cliente:', error);
      setError(error.response?.data?.message || 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-form-container">
      <h3>Agregar Nuevo Cliente</h3>
      {error && <div className="error-message">{error}</div>}
      <form className="customer-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="cus_name">Nombre/Razón Social *</label>
          <input
            type="text"
            id="cus_name"
            name="cus_name"
            value={formData.cus_name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="cus_rfc">RFC</label>
          <input
            type="text"
            id="cus_rfc"
            name="cus_rfc"
            value={formData.cus_rfc}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cus_domicilio">Domicilio</label>
          <textarea
            id="cus_domicilio"
            name="cus_domicilio"
            rows="3"
            value={formData.cus_domicilio}
            onChange={handleChange}
          ></textarea>
        </div>
        <div className="form-group">
          <label htmlFor="cus_zipcode">Código Postal</label>
          <input
            type="text"
            id="cus_zipcode"
            name="cus_zipcode"
            value={formData.cus_zipcode}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="regimenfiscal_id">Régimen Fiscal *</label>
          <select
            id="regimenfiscal_id"
            name="regimenfiscal_id"
            value={formData.regimenfiscal_id}
            onChange={handleChange}
            required
            disabled={loadingRegimenes}
          >
            <option value="">Seleccione un régimen fiscal</option>
            {regimenesFiscales.map(regimen => (
              <option key={regimen.id} value={regimen.id}>
                {regimen.rf_codigo} - {regimen.rf_nombreregimen}
              </option>
            ))}
          </select>
          {loadingRegimenes && <div className="loading-indicator">Cargando regímenes fiscales...</div>}
        </div>
        <div className="form-group">
          <label htmlFor="cus_email">Email</label>
          <input
            type="email"
            id="cus_email"
            name="cus_email"
            value={formData.cus_email}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cus_phone">Teléfono</label>
          <input
            type="tel"
            id="cus_phone"
            name="cus_phone"
            value={formData.cus_phone}
            onChange={handleChange}
          />
        </div>
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
